import { useUpdateVisioReviewConfig, useVisioReviewConfig } from '@gozoki/api'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { ArrowLeft, Brain, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import Page from '~/components/Page'
import { PendingEvaluationsTab } from '~/components/routes/visioReviews/PendingEvaluationsTab'
import {
  DEFAULT_THRESHOLDS,
  kioskTypeLabel,
} from '~/components/routes/visioReviews/visioReviews.utils'
import { Button } from '~/components/ui/button'
import {
  DateRangePicker,
  type DateRangePreset,
  DEFAULT_PRESETS,
  getRangeForPreset,
} from '~/components/ui/date-range-picker'
import { Card } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { Textarea } from '~/components/ui/textarea'

const isProduction =
  import.meta.env.VITE_BACKEND_API_ENDPOINT?.includes('api.lucie-la-cabane-autonome.fr') &&
  !import.meta.env.VITE_BACKEND_API_ENDPOINT?.includes('staging')

export const Route = createFileRoute('/_authenticated/visio-reviews/admin')({
  beforeLoad: () => {
    if (isProduction) throw redirect({ to: '/dashboard' })
  },
  component: VisioReviewsAdminPage,
})

function VisioReviewsAdminPage() {
  const navigate = Route.useNavigate()

  const [activeTab, setActiveTab] = useState('evaluation')
  const [preset, setPreset] = useState<DateRangePreset | null>('all')
  const [dateRange, setDateRange] = useState(() => getRangeForPreset('all'))

  const { data: config } = useVisioReviewConfig()
  const { mutate: updateConfig, isPending: isSavingConfig } = useUpdateVisioReviewConfig()

  const [problemPrompt, setProblemPrompt] = useState('')
  const [openPrompt, setOpenPrompt] = useState('')
  const [lowThreshold, setLowThreshold] = useState(DEFAULT_THRESHOLDS.low)
  const [highThreshold, setHighThreshold] = useState(DEFAULT_THRESHOLDS.high)

  useEffect(() => {
    if (config) {
      setProblemPrompt(config.problemPrompt ?? '')
      setOpenPrompt(config.openPrompt ?? '')
      setLowThreshold(config.lowGradeThreshold ?? DEFAULT_THRESHOLDS.low)
      setHighThreshold(config.highGradeThreshold ?? DEFAULT_THRESHOLDS.high)
    }
  }, [config])

  const handleSavePrompts = () => {
    updateConfig({
      problemPrompt: problemPrompt || null,
      openPrompt: openPrompt || null,
    })
  }

  const handleSaveThresholds = () => {
    if (lowThreshold >= highThreshold) {
      toast.error('Le seuil faible doit être inférieur au seuil excellent')
      return
    }
    updateConfig({
      lowGradeThreshold: lowThreshold,
      highGradeThreshold: highThreshold,
    })
  }

  return (
    <Page>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Administration — Contrôle Qualité</h1>
          <Button variant="outline" onClick={() => navigate({ to: '/visio-reviews' })}>
            <ArrowLeft className="h-4 w-4" />
            Retour au dashboard
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <TabsList>
              <TabsTrigger value="evaluation">Échanges à Évaluer</TabsTrigger>
              <TabsTrigger value="ai-config" className="flex items-center gap-2">
                <Brain className="h-4 w-4" />
                Configuration IA
              </TabsTrigger>
            </TabsList>
            {activeTab === 'evaluation' && (
              <DateRangePicker<DateRangePreset>
                value={dateRange}
                activePreset={preset}
                presets={DEFAULT_PRESETS}
                onChange={(range, newPreset) => {
                  setDateRange(range)
                  setPreset(newPreset)
                }}
              />
            )}
          </div>

          <TabsContent value="evaluation" className="mt-6">
            <PendingEvaluationsTab dateRange={dateRange} preset={preset} />
          </TabsContent>

          <TabsContent value="ai-config" className="mt-6 space-y-6">
            <Card className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="mb-2 font-semibold">Critères d'évaluation</h3>
                  <p className="text-muted-foreground mb-4 text-sm">
                    Définissez les critères de notation utilisés par l'IA pour évaluer les
                    opérateurs. Ces critères seront insérés dans le prompt d'évaluation de l'IA.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Critères — {kioskTypeLabel('CHECK_OUT')}
                      <span className="text-muted-foreground ml-1 font-normal">
                        (borne de paiement)
                      </span>
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isSavingConfig}
                      onClick={() => updateConfig({ problemPrompt: null })}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Réinitialiser
                    </Button>
                  </div>
                  <Textarea
                    value={problemPrompt}
                    onChange={(e) => setProblemPrompt(e.target.value)}
                    rows={8}
                    className="font-mono text-sm"
                    placeholder="Critères de notation pour les interactions liées à un problème client..."
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Critères — {kioskTypeLabel('CHECK_IN')}
                      <span className="text-muted-foreground ml-1 font-normal">
                        (kiosque d'entrée)
                      </span>
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isSavingConfig}
                      onClick={() => updateConfig({ openPrompt: null })}
                    >
                      <RotateCcw className="h-3 w-3" />
                      Réinitialiser
                    </Button>
                  </div>
                  <Textarea
                    value={openPrompt}
                    onChange={(e) => setOpenPrompt(e.target.value)}
                    rows={8}
                    className="font-mono text-sm"
                    placeholder="Critères de notation pour les interactions d'ouverture de porte..."
                  />
                </div>

                <div className="flex gap-3">
                  <Button onClick={handleSavePrompts} disabled={isSavingConfig}>
                    {isSavingConfig ? 'Enregistrement...' : 'Enregistrer les critères'}
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="mb-4 font-semibold">Paramètres d'Analyse</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm">Seuil note faible (alerte)</label>
                  <Input
                    type="number"
                    value={lowThreshold}
                    onChange={(e) => setLowThreshold(Number(e.target.value))}
                    min={1}
                    max={10}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm">Seuil note excellente</label>
                  <Input
                    type="number"
                    value={highThreshold}
                    onChange={(e) => setHighThreshold(Number(e.target.value))}
                    min={1}
                    max={10}
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={handleSaveThresholds} disabled={isSavingConfig}>
                  {isSavingConfig ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Page>
  )
}
